import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import ActivityCard from './components/ActivityCard';
import ActivityFormModal from './components/ActivityFormModal';
import { activityService } from '../../../services/activity.service';

type FilterType = 'all' | 'webinar' | 'workshop' | 'training';
type FilterStatus = 'all' | 'active' | 'ended';

const typeLabels: Record<string, string> = {
  all: 'הכל', webinar: 'וובינרים', workshop: 'סדנאות', training: 'הכשרות',
};

export default function ActivitiesPage() {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editActivity, setEditActivity] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadActivities = () => {
    setLoading(true);
    activityService.list().then((data) => {
      setActivities(data);
      setLoading(false);
    });
  };

  useEffect(() => { loadActivities(); }, []);

  const filtered = activities.filter((a) => {
    const matchType = filterType === 'all' || a.type?.name === filterType;
    const matchStatus = filterStatus === 'all' || a.status === filterStatus;
    const matchSearch = a.title.includes(search) || a.location.includes(search);
    return matchType && matchStatus && matchSearch;
  });

  const handleEdit = (id: number) => {
    const act = activities.find((a) => a.id === id);
    if (act) { setEditActivity(act); setShowModal(true); }
  };

  const handleDelete = (id: number) => setDeleteConfirm(id);

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await activityService.delete(deleteConfirm);
      setActivities((prev) => prev.filter((a) => a.id !== deleteConfirm));
      setDeleteConfirm(null);
    }
  };

  const handleSave = async (data: any) => {
    if (editActivity) {
      const updated = await activityService.update(editActivity.id, data);
      setActivities((prev) => prev.map((a) => (a.id === editActivity.id ? updated : a)));
    } else {
      const created = await activityService.create(data);
      setActivities((prev) => [created, ...prev]);
    }
    setShowModal(false);
    setEditActivity(null);
  };

  return (
    <AdminLayout title="פעילויות" subtitle="ניהול וובינרים, סדנאות והכשרות">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="relative flex items-center">
          <div className="w-4 h-4 flex items-center justify-center absolute right-3 text-gray-400"><i className="ri-search-line text-sm"></i></div>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש פעילות..."
            className="pr-9 pl-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 bg-white focus:outline-none focus:border-rose-300 w-56 transition-all" dir="rtl" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border-2 border-rose-100 rounded-xl p-1">
            {(['all', 'webinar', 'workshop', 'training'] as FilterType[]).map((t) => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${filterType === t ? 'bg-rose-400 text-white' : 'text-gray-500 hover:bg-rose-50'}`}>{typeLabels[t]}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-white border-2 border-rose-100 rounded-xl p-1">
            {(['all', 'active', 'ended'] as FilterStatus[]).map((s) => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-rose-400 text-white' : 'text-gray-500 hover:bg-rose-50'}`}>
                {s === 'all' ? 'כל הסטטוסים' : s === 'active' ? 'פעיל' : 'הסתיים'}</button>
            ))}
          </div>
          <button onClick={() => { setEditActivity(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base"></i></div>
            הוסף פעילות
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4">{filtered.length} פעילויות נמצאו</p>

      {loading ? (
        <div className="flex justify-center py-20"><p className="text-gray-500">טוען...</p></div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((activity) => (
            <ActivityCard key={activity.id} activity={activity} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-rose-50 border-2 border-rose-100 mb-4"><i className="ri-calendar-line text-3xl text-rose-300"></i></div>
          <p className="text-gray-500 font-semibold">לא נמצאו פעילויות</p>
          <p className="text-sm text-gray-400 mt-1">נסה לשנות את הפילטרים או הוסף פעילות חדשה</p>
        </div>
      )}

      {showModal && <ActivityFormModal activity={editActivity} onClose={() => { setShowModal(false); setEditActivity(null); }} onSave={handleSave} />}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-50 border-2 border-red-100 mx-auto mb-4"><i className="ri-delete-bin-line text-2xl text-red-400"></i></div>
            <h3 className="font-bold text-gray-800 mb-2">מחיקת פעילות</h3>
            <p className="text-sm text-gray-500 mb-5">האם אתה בטוח שברצונך למחוק פעילות זו? פעולה זו אינה ניתנת לביטול.</p>
            <div className="flex gap-3">
              <button onClick={confirmDelete} className="flex-1 py-2.5 rounded-xl bg-red-400 text-white text-sm font-bold hover:bg-red-500 transition-all cursor-pointer whitespace-nowrap">מחק</button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">ביטול</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
